import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, RefreshCw, LogIn, AlertCircle } from 'lucide-react';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  captcha_answer: z.number().min(1, 'Please solve the CAPTCHA'),
});

export const LoginForm = ({ onSuccess, onSwitchToSignup }) => {
  const { login, getCaptcha } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const captchaAnswer = watch('captcha_answer');

  useEffect(() => {
    loadCaptcha();
  }, []);

  const loadCaptcha = async (retryCount = 0) => {
    console.log('📋 Loading CAPTCHA for login... (attempt:', retryCount + 1, ')');
    const result = await getCaptcha();
    console.log('Login CAPTCHA result:', result);
    if (result.success) {
      setCaptcha(result.data);
      setValue('captcha_answer', '');
      setError(''); // Clear any previous errors
      console.log('✅ CAPTCHA loaded successfully:', result.data);
    } else {
      console.error('❌ Failed to load CAPTCHA:', result.error);
      // Retry up to 2 times with a delay
      if (retryCount < 2) {
        console.log('🔄 Retrying CAPTCHA load in 1 second...');
        setTimeout(() => loadCaptcha(retryCount + 1), 1000);
      } else {
        setError(`Failed to load security check: ${result.error}. Please refresh the page.`);
      }
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    // Ensure we have a valid CAPTCHA session
    if (!captcha || !captcha.session_id) {
      setError('CAPTCHA session expired. Please refresh.');
      await loadCaptcha();
      setLoading(false);
      return;
    }

    const loginData = {
      email: data.email,
      password: data.password,
      captcha_session_id: captcha.session_id,
      captcha_answer: parseInt(data.captcha_answer),
    };

    console.log('Login data being sent:', {
      ...loginData,
      password: '[HIDDEN]'
    });

    const result = await login(loginData);
    
    if (result.success) {
      console.log('✅ Login success, redirecting...');
      // Redirect based on user role (data contains the full response with user object)
      const userRole = result.data?.user?.role;
      console.log('User role:', userRole);
      if (userRole === 'admin' || userRole === 'sub-admin') {
        // Admin users go to admin dashboard
        window.location.href = '/admin';
      } else if (userRole === 'siso') {
        // SISO users go to SISO dashboard
        window.location.href = '/siso-dashboard';
      } else if (userRole === 'director' || userRole === 'dd_supervision' || userRole === 'secretary' || userRole === 'hrm' || userRole === 'personnel' || userRole === 'supervision_team') {
        // Directorate users go to director dashboard
        window.location.href = '/director-dashboard';
      } else if (userRole === 'other_ges_staff') {
        // Other GES Staff go to their dashboard
        window.location.href = '/ges-staff-dashboard';
      } else {
        // Teachers and headteachers go to dashboard switcher
        window.location.href = '/dashboard';
      }
    } else {
      setError(result.error);
      // Reload captcha on error
      loadCaptcha();
    }
    
    setLoading(false);
  };

  return (
    <Card className="w-full max-w-md mx-auto border-0 shadow-2xl bg-white/95 backdrop-blur-sm max-h-[90vh] flex flex-col">
      <CardHeader className="space-y-2 text-center flex-shrink-0 py-4">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-pink-500 rounded-full flex items-center justify-center mx-auto">
          <LogIn className="h-6 w-6 text-white" />
        </div>
        <CardTitle className="text-xl font-bold bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
          Welcome Back
        </CardTitle>
        <CardDescription className="text-sm text-gray-600">
          Sign in to your EduLegalHub account
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4 overflow-y-auto flex-1 px-6">
        {error && (
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700">{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          {/* Email Field */}
          <div className="space-y-1">
            <Label htmlFor="email" className="text-sm font-medium text-gray-700">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              data-testid="login-email-input"
              className="h-10 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              {...register('email')}
            />
            {errors.email && (
              <p className="text-xs text-red-600">{errors.email.message}</p>
            )}
          </div>

          {/* Password Field */}
          <div className="space-y-1">
            <Label htmlFor="password" className="text-sm font-medium text-gray-700">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                data-testid="login-password-input"
                className="h-10 pr-10 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs text-red-600">{errors.password.message}</p>
            )}
          </div>

          {/* CAPTCHA Field */}
          <div className="space-y-1">
            <Label htmlFor="captcha" className="text-sm font-medium text-gray-700">
              Security Check
            </Label>
            <div className="flex items-center space-x-2">
              <div className="flex-1">
                <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded border">
                  <span className="text-sm font-mono font-bold text-gray-800" data-testid="login-captcha-question">
                    {captcha?.question || 'Loading...'}
                  </span>
                  <button
                    type="button"
                    onClick={loadCaptcha}
                    className="p-1 hover:bg-gray-200 rounded"
                    disabled={!captcha}
                  >
                    <RefreshCw className="h-3 w-3 text-gray-600" />
                  </button>
                </div>
              </div>
              <div className="w-20">
                <Input
                  id="captcha"
                  type="number"
                  placeholder="Answer"
                  data-testid="login-captcha-input"
                  className="h-10 text-center text-sm font-mono border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  {...register('captcha_answer', { valueAsNumber: true })}
                />
              </div>
            </div>
            {errors.captcha_answer && (
              <p className="text-xs text-red-600">{errors.captcha_answer.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting || loading || !captcha}
            data-testid="login-submit-button"
            className="w-full h-10 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 mt-4"
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Signing In...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <LogIn className="h-4 w-4" />
                <span>Sign In</span>
              </div>
            )}
          </Button>
        </form>

        {/* Switch to Signup */}
        <div className="text-center pt-3 border-t border-gray-200 flex-shrink-0">
          <p className="text-sm text-gray-600">
            Don&apos;t have an account?{' '}
            <button
              type="button"
              onClick={onSwitchToSignup}
              className="text-blue-600 hover:text-blue-700 font-semibold transition-colors"
            >
              Sign up here
            </button>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};